import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Segment from "components/base/Segment";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { IoIosList, IoMdGrid } from "react-icons/io";
import { useNavigate, useSearchParams } from "react-router-dom";
import { searchByProp } from "utils/search";
import GearListGridView from "./GearListGridView";
import GearListTableView from "./GearListTableView";
import { CREATE_GEAR, FETCH_GEARS } from "./graphql";

const VIEW_TYPES = [
  {
    icon: <IoIosList />,
    value: "table",
  },
  {
    icon: <IoMdGrid />,
    value: "grid",
  },
];

const GearListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    viewType: "table",
    q: "",
  });
  const { loading, error, data } = useQuery(FETCH_GEARS, {
    fetchPolicy: "cache-and-network",
  });
  const viewType = searchParams.get("viewType") || "table";
  const q = searchParams.get("q") || "";

  const navigate = useNavigate();
  const [create, createRes] = useMutation(CREATE_GEAR, {
    onCompleted(res) {
      Alert("success", "Gear created successfully");
      navigate(`/gears/${res.createGear.product.id}`);
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const gears = data.products.results
    .filter((i) => searchByProp(i, ["name"], q))
    .map((i) => {
      const gearData = i.gearData ? JSON.parse(i.gearData) : null;
      return {
        ...i,
        supplierName: gearData?.supplierName || null,
        moq: gearData?.moq || null,
        leadtime: gearData?.leadtime || null,
        prices: gearData?.prices || null,
        currency: gearData?.currency || null,
        image: i.imageUrls?.[0] || null,
      };
    });

  function createItemHandler() {
    const name = window.prompt("Enter item name:");
    if (name) {
      create({ variables: { name } });
    }
  }

  return (
    <Page
      title="Marketing Gears"
      subtitle={
        <div className="text-orange-600 font-semibold">
          We are currently working on improving our marketing gear management.
          Data in this page might not be accurate.
        </div>
      }
      staticHeader
      className="h-screen"
    >
      <div className="p-6 flex flex-col flex-1 overflow-auto">
        <div className="flex justify-between items-center space-x-6">
          <div className="flex-1">
            <SearchBar
              placeholder="Search gears by name"
              initialQuery={q}
              onChange={(value) => setSearchParams({ q: value, viewType })}
            />
          </div>

          <div>
            <Segment
              options={VIEW_TYPES}
              value={searchParams.get("viewType")}
              onChange={(value) => setSearchParams({ q, viewType: value })}
            />
          </div>

          <div>
            <Button onClick={createItemHandler} loading={createRes.loading}>
              + Create Item
            </Button>
          </div>
        </div>

        <div className="card mt-4 flex-1 p-0 overflow-auto text-sm">
          {viewType === "grid" && <GearListGridView gears={gears} />}
          {viewType === "table" && <GearListTableView gears={gears} />}
        </div>
      </div>
    </Page>
  );
};

export default GearListPage;
